import { Alert, Divider, Skeleton, Stack, Text } from '@billda/design-system';
import { usePlotDetails } from '../../../hooks/usePlotDetails';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { InfoRow } from '../atoms/InfoRow';
import { PagedActivityTimeline } from '../molecules/PagedActivityTimeline';
import { usePlotEventsTimeline } from '../../../hooks/usePlotEventsTimeline';
import { formatCurrency } from '../../../../../shared/utils/currency';

interface PlotDetailsPanelProps {
  plotId: number;
}

export function PlotDetailsPanel({ plotId }: PlotDetailsPanelProps) {
  const { siteId } = useSitePanelState();
  const siteIdNum = siteId ? parseInt(siteId, 10) : null;
  const { data: plot, error } = usePlotDetails(siteIdNum, plotId);

  const {
    events,
    totalCount: timelineTotalCount,
    hasMore: timelineHasMore,
    isLoading: timelineLoading,
    isLoadingMore: timelineLoadingMore,
    loadMore: timelineLoadMore,
  } = usePlotEventsTimeline(siteIdNum, plotId);

  if (error && !plot) {
    return (
      <div data-testid="plot-details-panel" className="p-6">
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load plot details'}
        </Alert>
      </div>
    );
  }

  if (!plot) {
    return (
      <div data-testid="plot-details-panel" className="p-6">
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={300} />
        </Stack>
      </div>
    );
  }

  const { financialMetrics } = plot;

  return (
    <div data-testid="plot-details-panel">
      <div className="p-6 pb-0">
        <Text variant="h6" className="leading-tight">
          {plot.name}
        </Text>
      </div>

      <div className="p-6">
        <Stack spacing={0}>
          <Text variant="overline" className="mb-1 block font-semibold">
            Details
          </Text>
          <InfoRow label="Plot Number" testId="plot-number">
            {plot.plotNumber}
          </InfoRow>
          <InfoRow label="Labour Items" testId="labour-item-count">
            {plot.labourItemCount}
          </InfoRow>

          {financialMetrics && (
            <>
              <Divider className="my-4" />
              <Text variant="overline" className="mb-1 block font-semibold">
                Financials
              </Text>
              <InfoRow label="Total Budget" testId="total-budget">
                {formatCurrency(financialMetrics.totalLabourPrice)}
              </InfoRow>
              <InfoRow label="Total Spent" testId="total-spent">
                {formatCurrency(financialMetrics.totalSpent)}
              </InfoRow>
              <InfoRow label="Remaining" testId="remaining">
                <Text
                  as="span"
                  variant="body2"
                  className={
                    financialMetrics.remaining >= 0
                      ? 'text-content-success'
                      : 'text-content-error'
                  }
                >
                  {formatCurrency(financialMetrics.remaining)}
                </Text>
              </InfoRow>
              <div className="mb-3">
                <div className="mb-1 flex justify-between">
                  <Text variant="body2" color="secondary">
                    Progress
                  </Text>
                  <Text variant="body2" className="font-semibold">
                    {financialMetrics.progress.toFixed(1)}%
                  </Text>
                </div>
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(
                    Math.min(financialMetrics.progress, 100)
                  )}
                  className="h-2 w-full overflow-hidden rounded-lg bg-surface-raised"
                >
                  <div
                    className={`h-full rounded-lg transition-[width] ${
                      financialMetrics.progress > 100
                        ? 'bg-surface-error'
                        : 'bg-surface-primary'
                    }`}
                    style={{
                      width: `${Math.min(financialMetrics.progress, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <Divider className="my-4" />
          <Text variant="overline" className="mb-1.5 block font-semibold">
            Activity
          </Text>
          <PagedActivityTimeline
            events={events}
            totalCount={timelineTotalCount}
            hasMore={timelineHasMore}
            isLoading={timelineLoading}
            isLoadingMore={timelineLoadingMore}
            loadMore={timelineLoadMore}
          />
        </Stack>
      </div>
    </div>
  );
}
